"use client"

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

export interface MultiSelectFilterOption<T extends string> {
  value: T
  label: string
}

export interface MultiSelectFilterProps<T extends string> {
  testIdPrefix: string
  label: string
  options: MultiSelectFilterOption<T>[]
  selected: T[]
  onChange: (next: T[]) => void
}

/**
 * Shared multi-select filter — a dropdown trigger + checklist of options, built on the
 * existing DropdownMenu primitives. Used by ListingsIndexPage (status) and
 * LeadsIndexPage (stage); reusable by any future page needing the same pattern.
 */
export function MultiSelectFilter<T extends string>({
  testIdPrefix,
  label,
  options,
  selected,
  onChange,
}: MultiSelectFilterProps<T>) {
  const triggerLabel =
    selected.length === 0
      ? `${label}: None`
      : selected.length === options.length
        ? `${label}: All`
        : `${label} (${selected.length})`

  const toggle = (value: T, checked: boolean) => {
    if (checked) {
      if (!selected.includes(value)) onChange([...selected, value])
    } else {
      onChange(selected.filter((v) => v !== value))
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`${testIdPrefix}-multi-select-trigger`}
        aria-label={label}
        className={cn(
          "inline-flex min-h-[44px] items-center justify-between gap-2 min-w-[140px]",
          "border border-border rounded-md px-3 py-1.5 text-sm bg-background text-foreground",
          "outline-none focus:ring-2 focus:ring-ring"
        )}
      >
        {triggerLabel}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            data-testid={`${testIdPrefix}-multi-select-option-${opt.value}`}
            checked={selected.includes(opt.value)}
            onCheckedChange={(checked) => toggle(opt.value, checked)}
            // Keep the menu open across successive clicks so several options can be
            // toggled in one interaction, matching a checklist rather than a single pick.
            onSelect={(e) => e.preventDefault()}
            className="min-h-[44px]"
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
